import { useState, useMemo } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  Button,
  Badge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from 'debug-time-machine-ui';
import { 
  Network,
  Play,
  Pause,
  Trash2,
  Download,
  Clock,
  AlertTriangle,
  CheckCircle2,
  TrendingUp,
  Globe,
  Activity,
  Zap
} from 'lucide-react';

interface NetworkRequest {
  id: string;
  method: string;
  url: string;
  status: number;
  duration: number;
  timestamp: number;
  success: boolean;
  size?: number;
  type?: string;
}

interface NetworkDashboardProps {
  requests: NetworkRequest[];
  isRecording: boolean;
  onToggleRecording: () => void;
  onClearHistory: () => void;
  onExportData: () => void;
}

const NetworkDashboard = ({ 
  requests, 
  isRecording, 
  onToggleRecording, 
  onClearHistory, 
  onExportData 
}: NetworkDashboardProps) => {
  const [filterStatus, setFilterStatus] = useState<'all' | 'success' | 'error'>('all');
  const [sortBy, setSortBy] = useState<'timestamp' | 'duration' | 'status'>('timestamp');

  // 네트워크 통계 계산
  const stats = useMemo(() => {
    const total = requests.length;
    const successful = requests.filter(r => r.success).length;
    const failed = total - successful;
    const avgDuration = total > 0 ? requests.reduce((sum, r) => sum + r.duration, 0) / total : 0;
    const totalSize = requests.reduce((sum, r) => sum + (r.size || 0), 0);
    
    return {
      total,
      successful,
      failed,
      successRate: total > 0 ? (successful / total) * 100 : 0,
      avgDuration,
      totalSize
    };
  }, [requests]);

  // 필터링 및 정렬된 요청
  const filteredRequests = useMemo(() => {
    let filtered = requests;

    // 상태별 필터링
    if (filterStatus === 'success') {
      filtered = filtered.filter(r => r.success);
    } else if (filterStatus === 'error') {
      filtered = filtered.filter(r => !r.success);
    }

    // 정렬
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'duration':
          return b.duration - a.duration;
        case 'status':
          return a.status - b.status;
        case 'timestamp':
        default:
          return b.timestamp - a.timestamp;
      }
    });
  }, [requests, filterStatus, sortBy]);

  const formatDuration = (ms: number) => `${ms}ms`;
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  const getStatusColor = (status: number) => {
    if (status >= 200 && status < 300) return 'text-emerald-600';
    if (status >= 300 && status < 400) return 'text-blue-600';
    if (status >= 400 && status < 500) return 'text-amber-600';
    return 'text-red-600';
  };

  const getMethodColor = (method: string) => {
    switch (method.toUpperCase()) {
      case 'GET': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'POST': return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200';
      case 'PUT': return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
      case 'DELETE': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-slate-100 text-slate-800 dark:bg-slate-900 dark:text-slate-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* 컨트롤 헤더 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Network className="w-5 h-5" />
            Network Monitor
          </h3>
          <Badge variant={isRecording ? 'default' : 'secondary'}>
            {isRecording ? 'Recording' : 'Paused'}
          </Badge>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant={isRecording ? 'destructive' : 'default'}
            size="sm"
            onClick={onToggleRecording}
          >
            {isRecording ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
            {isRecording ? 'Pause' : 'Record'}
          </Button>
          
          <Button variant="outline" size="sm" onClick={onClearHistory}>
            <Trash2 className="w-4 h-4 mr-2" />
            Clear
          </Button>
          
          <Button variant="outline" size="sm" onClick={onExportData}>
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* 네트워크 통계 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Requests</CardTitle>
            <Activity className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">
              {stats.successful} successful, {stats.failed} failed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
            {stats.successRate >= 95 ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
            ) : (
              <AlertTriangle className="w-4 h-4 text-amber-500" />
            )}
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${stats.successRate >= 95 ? 'text-emerald-600' : 'text-amber-600'}`}>
              {stats.successRate.toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">
              Network reliability
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Response Time</CardTitle>
            <Clock className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${stats.avgDuration < 200 ? 'text-emerald-600' : stats.avgDuration < 1000 ? 'text-amber-600' : 'text-red-600'}`}>
              {formatDuration(stats.avgDuration)}
            </div>
            <p className="text-xs text-muted-foreground">
              Average latency
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Data Transferred</CardTitle>
            <Globe className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatSize(stats.totalSize)}</div>
            <p className="text-xs text-muted-foreground">
              Total bandwidth used
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="requests" className="space-y-4">
        <TabsList>
          <TabsTrigger value="requests">Requests</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="requests" className="space-y-4">
          {/* 필터 및 정렬 */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Filter:</span>
              <Button
                variant={filterStatus === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterStatus('all')}
              >
                All ({stats.total})
              </Button>
              <Button
                variant={filterStatus === 'success' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterStatus('success')}
              >
                Success ({stats.successful})
              </Button>
              <Button
                variant={filterStatus === 'error' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterStatus('error')}
              >
                Errors ({stats.failed})
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="px-3 py-1 text-sm border rounded-md"
              >
                <option value="timestamp">Time</option>
                <option value="duration">Duration</option>
                <option value="status">Status</option>
              </select>
            </div>
          </div>

          {/* 요청 목록 */}
          <Card>
            <CardHeader>
              <CardTitle>Network Requests ({filteredRequests.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {filteredRequests.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredRequests.map((request, index) => (
                    <div key={`${request.id}-${index}`} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <Badge className={`text-xs font-mono ${getMethodColor(request.method)}`}>
                          {request.method}
                        </Badge>
                        
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">
                            {request.url.split('/').pop() || request.url}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {request.url}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 text-sm">
                        <span className={`font-mono ${getStatusColor(request.status)}`}>
                          {request.status}
                        </span>
                        
                        <span className={`font-mono ${request.duration > 1000 ? 'text-red-600' : request.duration > 500 ? 'text-amber-600' : 'text-emerald-600'}`}>
                          {formatDuration(request.duration)}
                        </span>
                        
                        {request.size && (
                          <span className="font-mono text-muted-foreground">
                            {formatSize(request.size)}
                          </span>
                        )}
                        
                        <span className="font-mono text-muted-foreground">
                          {new Date(request.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Network className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium mb-2">No Network Requests</p>
                  <p className="text-sm">
                    {!isRecording 
                      ? "Start recording to capture network activity" 
                      : "Make some API calls to see them here"
                    }
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* 응답시간 분포 */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-orange-500" />
                  Response Time Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                {requests.length > 0 ? (
                  <div className="space-y-3">
                    {['< 200ms', '200ms - 1s', '1s - 5s', '> 5s'].map((range, index) => {
                      const counts = [
                        requests.filter(r => r.duration < 200).length,
                        requests.filter(r => r.duration >= 200 && r.duration < 1000).length,
                        requests.filter(r => r.duration >= 1000 && r.duration < 5000).length,
                        requests.filter(r => r.duration >= 5000).length,
                      ];
                      const count = counts[index];
                      const percentage = requests.length > 0 ? (count / requests.length) * 100 : 0;
                      
                      return (
                        <div key={range} className="flex items-center justify-between">
                          <span className="text-sm">{range}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-20 bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                              <div 
                                className="bg-blue-500 h-2 rounded-full" 
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span className="text-sm font-mono w-8 text-right">{count}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No data available</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* HTTP 상태 코드 분포 */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-purple-500" />
                  Status Code Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                {requests.length > 0 ? (
                  <div className="space-y-3">
                    {['2xx Success', '3xx Redirect', '4xx Client Error', '5xx Server Error'].map((range, index) => {
                      const counts = [
                        requests.filter(r => r.status >= 200 && r.status < 300).length,
                        requests.filter(r => r.status >= 300 && r.status < 400).length,
                        requests.filter(r => r.status >= 400 && r.status < 500).length,
                        requests.filter(r => r.status >= 500).length,
                      ];
                      const colors = ['bg-emerald-500', 'bg-blue-500', 'bg-amber-500', 'bg-red-500'];
                      const count = counts[index];
                      const percentage = requests.length > 0 ? (count / requests.length) * 100 : 0;
                      
                      return (
                        <div key={range} className="flex items-center justify-between">
                          <span className="text-sm">{range}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-20 bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                              <div 
                                className={`${colors[index]} h-2 rounded-full`}
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <span className="text-sm font-mono w-8 text-right">{count}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No data available</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default NetworkDashboard;
